import React from 'react';
import { Link } from 'react-router-dom';
import { LuZap, LuChevronRight } from 'react-icons/lu';
import Card from '../../ui/Card/Card';
import Badge from '../../ui/Badge/Badge';
import CurrencyAmount from '../../ui/CurrencyAmount/CurrencyAmount';
import { ocppStatusCopy } from '../../../utils/statusCopy';
import { formatElapsedTime } from '../../../utils/formatElapsedTime';
import './ActiveSessionCard.less';

/**
 * Dashboard hero card summarising the user's active session. Reads
 * straight from SessionContext.state so the numbers always match the
 * wizard.
 */
function ActiveSessionCard({ session }) {
  if (!session?.sessionUid) return null;

  const live = session.live || {};
  const status = ocppStatusCopy(live.status);
  const energy = typeof live.energyKWh === 'number' ? live.energyKWh : null;
  const power = typeof live.powerKW === 'number' && live.powerKW > 0 ? live.powerKW : null;
  const startTs = live.startTime ? new Date(live.startTime).getTime() : null;
  const elapsedSec = startTs ? Math.max(0, Math.floor((Date.now() - startTs) / 1000)) : null;
  const budgetCents = session.sessionBudgetCents ?? session.authorizedAmount ?? null;
  const spentCents = typeof energy === 'number' && session.pricingSnapshot?.rate_per_kwh
    ? Math.round(energy * Number(session.pricingSnapshot.rate_per_kwh) * 100)
    : null;
  const currency = session.currency || session.pricingSnapshot?.currency || 'BBD';

  return (
    <Card className="gc-active-session">
      <div className="gc-active-session-head">
        <div className="gc-active-session-lead">
          <span className="gc-active-session-icon" aria-hidden="true"><LuZap /></span>
          <div>
            <p className="gc-active-session-eyebrow">Active session</p>
            <h2 className="gc-active-session-title">
              {session.stationMeta?.name || 'Charging in progress'}
            </h2>
            {session.stationMeta?.location && (
              <p className="gc-active-session-sub">{session.stationMeta.location}</p>
            )}
          </div>
        </div>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>

      <dl className="gc-active-session-metrics">
        <div>
          <dt>Energy</dt>
          <dd>{energy != null ? `${energy.toFixed(2)} kWh` : '—'}</dd>
        </div>
        <div>
          <dt>Power</dt>
          <dd>{power != null ? `${power.toFixed(1)} kW` : '—'}</dd>
        </div>
        <div>
          <dt>Elapsed</dt>
          <dd>{elapsedSec != null ? formatElapsedTime(elapsedSec) : '—'}</dd>
        </div>
        <div>
          <dt>Estimated cost</dt>
          <dd>
            <CurrencyAmount cents={spentCents} currency={currency} />
            {budgetCents ? (
              <span className="gc-active-session-budget">
                {' / '}<CurrencyAmount cents={budgetCents} currency={currency} />
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      <Link to="/charge/active" className="gc-active-session-cta">
        <span>Open charging session</span>
        <LuChevronRight aria-hidden="true" />
      </Link>
    </Card>
  );
}

export default ActiveSessionCard;
